import { ToastMessage } from '@cokitchen/cokitchen-components';
import React, { useState, useEffect, useCallback } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-icon.svg';
import './styles.scss';
import { CostSettingsType } from 'models/pricing.model';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { debounce, formatCurrency } from 'utils/functions';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import EditCostSetting from './edit-cost-setting';

export const DefaultCostSettings = {
  id: '',
  hr_tax: '',
  vat: '',
  labour_cost: '',
  gas_consumption: '',
  min_selling_price_margin: '',
  projected_selling_price_margin: '',
  created_at: '',
  updated_at: ''
};
export type CostSettingType = { name: string; value: string };

const CostSettings = () => {
  const [costSettings, setCostSettings] = useState<CostSettingsType>(DefaultCostSettings);
  const [isFetchingCostSettings, setIsFetchingCostSettings] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<CostSettingType>({ name: '', value: '' });
  const [loading, setLoading] = useState(false);
  const handleEdit = (item: CostSettingType) => {
    if (loading) {
      return;
    }
    setItemToEdit(item);
  };

  const fetchCostSettings = async () => {
    try {
      setIsFetchingCostSettings(true);
      const response = await apiService.get('pricing');
      if (response) {
        setCostSettings(response?.data);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching meals'}
          type='error'
        />
      );
    } finally {
      setIsFetchingCostSettings(false);
    }
  };
  const nameText = itemToEdit?.name?.replaceAll('_', ' ');
  const handleOnChange = (val: string) => {
    setItemToEdit({ ...itemToEdit, value: val });
  };
  const editCostSetting = async (e: any) => {
    e.preventDefault();
    const data = {
      hr_tax: parseFloat(costSettings.hr_tax),
      vat: parseFloat(costSettings.vat),
      labour_cost: parseFloat(costSettings.labour_cost),
      gas_consumption: parseFloat(costSettings.gas_consumption),
      min_selling_price_margin: parseFloat(costSettings.min_selling_price_margin),
      projected_selling_price_margin: parseFloat(costSettings.projected_selling_price_margin),
      [itemToEdit.name]: parseFloat(itemToEdit.value)
    };
    try {
      setLoading(true);
      const res = await apiService.post(`pricing`, {
        data,
        useToken: true
      });
      if (res) {
        fetchCostSettings();
        toast(<ToastMessage message={`${nameText} updated successfully`} type='success' />);
      }
    } catch (error: any) {
      toast(
        <ToastMessage
          message={error.response.data.message || `${nameText} update failed`}
          type='error'
        />
      );
    } finally {
      setLoading(false);
    }
  };
  const debounceFetchCostSettings = useCallback(debounce(fetchCostSettings, 1000), []);

  useEffect(() => {
    setIsFetchingCostSettings(true);
    clearVendorsList();
    debounceFetchCostSettings();
  }, []);

  const clearVendorsList = () => {
    setCostSettings(DefaultCostSettings);
  };

  return (
    <div className='px-2 md:px-6 mt-2 mb-10 vendors relative'>
      <div className='flex flex-col-reverse md:flex-row justify-start md:justify-between items-start gap-7 '>
        <div className='flex flex-col justify-start items-start gap-7 md:basis-[60%] '>
          <div className='flex justify-between items-center mb-8'>
            <div>
              <h4 className='text-[#2E3031] text-2xl font-semibold'>Cost settings</h4>
              <p className='text-[#6F7376] text-sm'>
                Update the cost settings as the policy changes
              </p>
            </div>
          </div>

          {isFetchingCostSettings ? (
            <div className='pt-24 absolute w-full'>
              <ActivityIndicator />
            </div>
          ) : (
            <>
              {Object.keys(costSettings)
                ?.filter((_) => _ !== 'created_at' && _ !== 'updated_at' && _ !== 'id')
                ?.map((item, i) => {
                  const value = costSettings[item as keyof CostSettingsType];

                  const isFixed = item === 'gas_consumption' || item === 'labour_cost';
                  return (
                    <div
                      key={i}
                      onClick={() => handleEdit({ name: item, value })}
                      className={`item-card ${
                        loading ? 'cursor-not-allowed' : 'cursor-pointer'
                      } bg-white rounded-lg px-3 py-2 flex gap-2 justify-between items-center  border-[0.8px] border-[#EAEBEB] ease-in-out transition-colors duration-300 w-full`}>
                      <div className='flex flex-col justify-start items-start gap-3'>
                        <span className='text-sm font-bold uppercase text-[#5E6164]'>
                          {item?.replaceAll('_', ' ')}
                        </span>

                        <span className='text-xs text-[#2E3031]'>
                          {isFixed ? formatCurrency(value) : value + '%'}
                        </span>
                      </div>
                      <button className='flex justify-center items-center gap-1 bg-white w-[30px] h-[30px] rounded-full opacity-0'>
                        <EditIcon className='w-[18px] h-[18px]' />
                      </button>
                    </div>
                  );
                })}
            </>
          )}
        </div>

        <div className='flex flex-col justify-start items-start gap-7 md:basis-[35%] '>
          {itemToEdit?.name ? (
            <EditCostSetting {...{ ...itemToEdit, loading, handleOnChange, editCostSetting }} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CostSettings;
